import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  IconArrowRight,
  IconArrowLeft,
  IconClose,
  IconInvoice,
  IconOffer,
  IconFolder,
  IconClients,
  IconProducts,
  IconUser,
  IconRoute,
} from '../../icons';
import { INVOICE_FORM_ROUTE } from '../../config/cockpitAccess';

interface TourStep {
  id: string;
  title: string;
  content: string;
  path: string;
  icon: React.ReactNode;
  route?: string;
  highlight?: string;
}

interface AppTourProps {
  onComplete: () => void;
}

const AppTour: React.FC<AppTourProps> = ({ onComplete }) => {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  // M-UX-G1: Tour ist jetzt sekundär (CompletionStep-Primär-CTA führt bereits direkt
  // zur Rechnungserstellung). Der "invoice"-Schritt bleibt daher konsistent mit dem
  // Aha-Moment an erster Stelle, statt mit "offer" zu beginnen.
  const tourSteps: TourStep[] = [
    {
      id: 'invoice',
      title: t('tour.invoice.title'),
      content: t('tour.invoice.content'),
      path: t('tour.invoice.path'),
      icon: <IconInvoice size={32} />,
      // M-UX-4.2: dieser Schritt highlightet das Rechnungsformular — folgt
      // INVOICE_FORM_ROUTE (Flag off identisch zu '/dashboard'). Tour-Ende
      // (handleNext/handleSkip, weiter unten) bleibt bewusst '/dashboard'
      // (Home → Cockpit bei Flag on) und wird HIER NICHT angefasst.
      route: INVOICE_FORM_ROUTE,
      highlight: 'invoice',
    },
    {
      id: 'offer',
      title: t('tour.offer.title'),
      content: t('tour.offer.content'),
      path: t('tour.offer.path'),
      icon: <IconOffer size={32} />,
      route: '/dashboard/angebote',
      highlight: 'offer',
    },
    {
      id: 'archive',
      title: t('tour.archive.title'),
      content: t('tour.archive.content'),
      path: t('tour.archive.path'),
      icon: <IconFolder size={32} />,
      route: '/dashboard/archiv',
      highlight: 'archive',
    },
    {
      id: 'customers',
      title: t('tour.customers.title'),
      content: t('tour.customers.content'),
      path: t('tour.customers.path'),
      icon: <IconClients size={32} />,
      route: '/dashboard/kunden',
      highlight: 'customers',
    },
    {
      id: 'products',
      title: t('tour.products.title'),
      content: t('tour.products.content'),
      path: t('tour.products.path'),
      icon: <IconProducts size={32} />,
      route: '/dashboard/produkte',
      highlight: 'products',
    },
    {
      id: 'profile',
      title: t('tour.profile.title'),
      content: t('tour.profile.content'),
      path: t('tour.profile.path'),
      icon: <IconUser size={32} />,
      route: '/dashboard/konto',
      highlight: 'profile',
    },
  ];

  const currentStep = tourSteps[currentStepIndex];
  const isLastStep = currentStepIndex === tourSteps.length - 1;
  const isFirstStep = currentStepIndex === 0;

  // Navigate to route when step changes
  useEffect(() => {
    if (currentStep.route) {
      navigate(currentStep.route, { replace: true });
    }
  }, [currentStep.route, navigate]);

  const handleNext = useCallback(() => {
    if (isLastStep) {
      setIsExiting(true);
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
        onComplete();
      }, 300);
    } else {
      setCurrentStepIndex(prev => prev + 1);
    }
  }, [isLastStep, navigate, onComplete]);

  const handlePrevious = useCallback(() => {
    if (!isFirstStep) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [isFirstStep]);


  const handleSkip = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      navigate('/dashboard', { replace: true });
      onComplete();
    }, 300);
  }, [navigate, onComplete]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'Escape') {
        handleSkip();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrevious, handleSkip]);

  return (
    <div 
      className={`fixed inset-0 z-[100] transition-opacity duration-300 ${
        isExiting ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Backdrop - halbtransparent damit App dahinter sichtbar ist */}
      <div className="absolute inset-0 bg-black/50" />
      
      {/* Tour Card */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div 
          className={`
            relative w-full max-w-lg bg-surface rounded-2xl shadow-2xl border border-hairline
            transform transition-all duration-300
            ${isExiting ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}
          `}
        >
          {/* Close Button */}
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 p-2 text-muted hover:text-primary transition-colors rounded-lg hover:bg-surface-raised"
          >
            <IconClose size={20} />
          </button>

          {/* Step Indicator */}
          <div className="absolute top-4 left-4 px-3 py-1 bg-surface-raised rounded-full text-sm text-muted">
            {currentStepIndex + 1} / {tourSteps.length}
          </div>

          {/* Content */}
          <div className="pt-16 pb-8 px-8">
            {/* Icon */}
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-indigo-500 to-[#0C1322] rounded-2xl flex items-center justify-center text-white shadow-lg">
              {currentStep.icon}
            </div>

            {/* Title */}
            <h3 className="text-2xl font-bold text-primary text-center mb-3">
              {currentStep.title}
            </h3>

            {/* Content */}
            <p className="text-secondary text-center leading-relaxed mb-4">
              {currentStep.content}
            </p>
            
            {/* Navigation Path */}
            <div className="bg-[rgb(var(--surface-raised-rgb)/0.8)] rounded-lg px-4 py-3 mb-8 border border-hairline">
              <div className="flex items-center justify-center text-sm">
                <IconRoute className="text-accent-ink mr-2 flex-shrink-0" />
                <span className="text-muted mr-2">{t('tour.howToGet')}</span>
                <span className="text-accent-ink font-medium">{currentStep.path}</span>
              </div>
            </div>

            {/* Progress Dots */}
            <div className="flex justify-center space-x-2 mb-8">
              {tourSteps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStepIndex(index)}
                  className={`
                    w-2.5 h-2.5 rounded-full transition-all duration-200
                    ${index === currentStepIndex 
                      ? 'bg-indigo-500 w-8' 
                      : index < currentStepIndex 
                        ? 'bg-indigo-500/50' 
                        : 'bg-surface-hover'
                    }
                  `}
                />
              ))}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center">
              <button
                onClick={handlePrevious}
                disabled={isFirstStep}
                className={`
                  flex items-center px-4 py-2 text-muted transition-colors
                  ${isFirstStep ? 'opacity-0 cursor-default' : 'hover:text-primary'}
                `}
              >
                <IconArrowLeft className="mr-2" />
                {t('tour.previous')}
              </button>

              <button
                onClick={handleNext}
                className="flex items-center px-6 py-3 bg-indigo-600 text-[#0C1322] font-medium rounded-xl hover:bg-indigo-700 transition-colors shadow-lg hover:shadow-indigo-500/30"
              >
                {isLastStep ? t('tour.finish') : t('tour.next')}
                {!isLastStep && <IconArrowRight className="ml-2" />}
              </button>
            </div>
          </div>

          {/* Skip Link */}
          <div className="border-t border-hairline py-4 text-center">
            <button
              onClick={handleSkip}
              className="text-faint hover:text-secondary text-sm transition-colors"
            >
              {t('tour.skip')}
            </button>
          </div>
        </div>
      </div>

      {/* Keyboard Hint */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-faint text-sm hidden sm:block">
        <span className="px-2 py-1 bg-surface-raised rounded mr-2">←</span>
        <span className="px-2 py-1 bg-surface-raised rounded mr-2">→</span>
        {t('tour.keyboardHint')}
      </div>
    </div>
  );
};

export default AppTour;

